import React, { useState } from 'react';
import BackOfficeLayout from '../../components/backoffice/BackOfficeLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { Mail, Eye } from 'lucide-react';

const TEMPLATES = [
  {
    id: 'welcome',
    name: 'Welcome Email',
    subject: 'Welcome to PayZen',
    trigger: 'On registration',
    active: true,
    body: `Hi {{name}},\n\nWelcome to PayZen! Your account has been created.\n\nYour temporary password is: {{temp_password}}\nPlease log in and change it immediately.\n\nBest regards,\nPayZen Team`,
  },
  {
    id: 'kyc-approved',
    name: 'KYC Approved',
    subject: 'Your KYC has been approved',
    trigger: 'On KYC approval',
    active: true,
    body: `Hi {{name}},\n\nGreat news! Your KYC / KYB verification has been approved.\nYou now have full access to Live mode on PayZen.\n\nPlease enable Two-Factor Authentication to secure your account.\n\nBest regards,\nPayZen Team`,
  },
  {
    id: 'kyc-rejected',
    name: 'KYC Rejected',
    subject: 'KYC verification update',
    trigger: 'On KYC rejection',
    active: true,
    body: `Hi {{name}},\n\nWe were unable to approve your KYC / KYB submission.\nReason: {{reason}}\n\nPlease review your details and re-submit. Contact support if you need help.\n\nBest regards,\nPayZen Team`,
  },
  {
    id: 'password-reset',
    name: 'Password Reset',
    subject: 'Reset your password',
    trigger: 'On password reset request',
    active: true,
    body: `Hi {{name}},\n\nA password reset was requested for your account. Use the link below to set a new password:\n\n{{reset_link}}\n\nThis link expires in 1 hour. If you didn't request this, you can ignore this email.\n\nBest regards,\nPayZen Team`,
  },
  {
    id: 'suspension',
    name: 'Account Suspended',
    subject: 'Your account has been suspended',
    trigger: 'On account suspension',
    active: true,
    body: `Hi {{name}},\n\nYour PayZen account has been suspended by an administrator.\nPlease contact your administrator for more information.\n\nBest regards,\nPayZen Team`,
  },
  {
    id: '2fa-reminder',
    name: '2FA Reminder',
    subject: 'Enable two-factor authentication',
    trigger: 'On Live login without 2FA',
    active: true,
    body: `Hi {{name}},\n\nTwo-factor authentication (2FA) is mandatory for production accounts.\nYou have {{skips_left}} skip(s) remaining before 2FA must be enabled.\n\nPlease go to Settings > Security to set it up.\n\nBest regards,\nPayZen Team`,
  },
];

export default function BackOfficeNotificationsTemplates() {
  const [preview, setPreview] = useState(null);

  return (
    <BackOfficeLayout>
      <div className="space-y-6">
        <div className="pb-4">
          <h1 className="text-2xl font-bold text-[#073B2E] flex items-center gap-2"><Mail className="w-7 h-7 text-[#0B6445]" />Email Templates</h1>
          <p className="text-[#0E1B16]/70 mt-2">Platform email templates sent to merchants and users.</p>
        </div>

        <Card className="border-[#DCE4E0] shadow-sm">
          <CardHeader className="pb-2"><CardTitle className="text-base text-[#073B2E]">{TEMPLATES.length} Templates</CardTitle></CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-[#DCE4E0] bg-[#F8FAF9]">
                  <th className="text-left py-3 px-4 font-semibold text-[#073B2E]">Template</th>
                  <th className="text-left py-3 px-4 font-semibold text-[#073B2E]">Subject</th>
                  <th className="text-left py-3 px-4 font-semibold text-[#073B2E]">Trigger</th>
                  <th className="text-center py-3 px-4 font-semibold text-[#073B2E]">Status</th>
                  <th className="text-right py-3 px-4 font-semibold text-[#073B2E]">Action</th>
                </tr></thead>
                <tbody>
                  {TEMPLATES.map((t) => (
                    <tr key={t.id} className="border-b border-[#DCE4E0]/60 hover:bg-[#F8FAFC]">
                      <td className="py-3 px-4 font-medium text-[#0F172A]">{t.name}</td>
                      <td className="py-3 px-4 text-slate-600">{t.subject}</td>
                      <td className="py-3 px-4 text-slate-500 text-xs">{t.trigger}</td>
                      <td className="py-3 px-4 text-center">
                        <Badge variant="outline" className={t.active ? 'bg-green-50 text-green-700 border-green-200' : 'bg-slate-50 text-slate-500 border-slate-200'}>{t.active ? 'Active' : 'Disabled'}</Badge>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <Button size="sm" variant="outline" onClick={() => setPreview(t)} className="border-[#1C472B] text-[#1C472B] hover:bg-[#E6F7F3] hover:text-[#073B2E]"><Eye className="w-3.5 h-3.5 mr-1" />Preview</Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        <Dialog open={!!preview} onOpenChange={() => setPreview(null)}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-[#0F172A]">{preview?.name}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div><span className="text-xs font-medium text-slate-500 uppercase">Subject</span><p className="text-sm font-medium mt-0.5">{preview?.subject}</p></div>
              <div><span className="text-xs font-medium text-slate-500 uppercase">Body</span><pre className="text-sm mt-1 bg-[#F8FAF9] border border-[#DCE4E0] rounded-lg p-3 whitespace-pre-wrap font-sans">{preview?.body}</pre></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setPreview(null)} className="border-slate-300 text-slate-700 hover:bg-slate-100 hover:text-slate-900">Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </BackOfficeLayout>
  );
}
